import {Chart as ChartJS, ArcElement, Tooltip, Legend, ChartData} from 'chart.js';
import {Card} from '@/shadcn_components/ui/card';
import {Doughnut} from 'react-chartjs-2';
import {apiService} from '@/lib/ApiService';
import {useQuery} from '@tanstack/react-query';
import Spinner from '@/components/basic/Spinner';
import {ProjectStatuses, ProjectStatusColors} from '../resources/projects/types';
import {TaskStatusColors, TaskStatuses} from '../resources/tasks/types';
import {Link} from 'react-router-dom';
import {CanAccess} from '@refinedev/core';
import {useEffect, useState} from 'react';

ChartJS.register(ArcElement, Tooltip, Legend);

// Interface for the API response
interface ProjectSummary {
  by_status: {
    [key: string]: number;
  };
  total: number;
}

// Interface for task summary API response
interface TaskSummary {
  by_status: {
    [key: string]: number;
  };
  total: number;
}

export default function Dashboard() {
  // Fetch project summary data from API
  const {
    data: projectSummary,
    isLoading,
    error,
  } = useQuery<any, any>({
    queryKey: ['project-summary'],
    queryFn: () => apiService.projects.summary(),
  });

  // Fetch task summary data from API
  const {
    data: taskSummary,
    isLoading: isTaskLoading,
    error: taskError,
  } = useQuery<any, any>({
    queryKey: ['task-summary'],
    queryFn: () => apiService.tasks.summary(),
  });

  // Track window size for responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="p-3 md:p-6">
      <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Tổng quan</h1>

      {/* Project Summary Charts */}
      <h2 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Dự án</h2>
      {isLoading ? (
        <div className="w-full flex justify-center p-6 md:p-10">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="w-full text-center p-6 md:p-10 text-red-500">Lỗi tải {error.message}</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-4 w-full">
          <CanAccess resource="projects" action="list">
            <ProjectDoughnutChart
              projectSummary={projectSummary.all_projects}
              label="Tất cả dự án"
              isMobile={isMobile}
            />
          </CanAccess>
          <ProjectDoughnutChart
            projectSummary={projectSummary.user_projects}
            label="Dự án tôi tham gia"
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Task Summary Charts */}
      <h2 className="text-lg md:text-xl font-semibold mt-6 md:mt-8 mb-3 md:mb-4">Công việc</h2>
      {isTaskLoading ? (
        <div className="w-full flex justify-center p-6 md:p-10">
          <Spinner size="lg" />
        </div>
      ) : taskError ? (
        <div className="w-full text-center p-6 md:p-10 text-red-500">Lỗi tải {taskError.message}</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-4 w-full">
          <CanAccess resource="tasks" action="list">
            <TaskDoughnutChart
              taskSummary={taskSummary.all_tasks}
              label="Tất cả công việc"
              isMobile={isMobile}
            />
          </CanAccess>
          <TaskDoughnutChart
            taskSummary={taskSummary.user_tasks}
            label="Công việc được giao"
            isMobile={isMobile}
          />
        </div>
      )}
    </div>
  );
}

const ProjectDoughnutChart = ({
  projectSummary,
  label,
  isMobile,
}: {
  label: string;
  projectSummary?: ProjectSummary;
  isMobile: boolean;
}) => {
  // Get status colors array for chart
  const statusColors = Object.values(ProjectStatusColors);

  // Process chart data after API response
  const chartData: ChartData<'doughnut'> = {
    labels: ProjectStatuses.map(status => status.label),
    datasets: [
      {
        data: ProjectStatuses.map(status => projectSummary?.by_status?.[status.id] || 0),
        backgroundColor: statusColors,
        borderWidth: 1,
      },
    ],
  };

  const total = projectSummary?.total || 0;
  const colors = chartData.datasets[0].backgroundColor;
  const values = chartData.datasets[0].data;

  const centerText = {
    id: 'centerText',
    beforeDraw(chart: any) {
      const {
        ctx,
        chartArea: {width, height},
      } = chart;
      ctx.save();
      ctx.font = 'bold 24px Arial';
      ctx.fillStyle = '#666';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(total.toString(), width / 2, height / 2);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#999';
      ctx.fillText('Tổng số', width / 2, height / 2 + 25);
    },
  };

  return (
    <Card className="p-4 md:p-6 w-full">
      <h3 className="text-lg font-semibold mb-4">{label || 'Dự án'}</h3>
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center gap-4 w-full`}>
        <div className={`${isMobile ? 'w-full' : 'w-1/2'} flex justify-center`}>
          <div style={{ maxWidth: '250px', width: '100%' }}>
            <Doughnut
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    display: false, // Hide the default legend
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const label = context.label || '';
                        const value = context.raw as number;
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      },
                    },
                  },
                },
                cutout: '70%', // Makes the doughnut hole bigger
              }}
              plugins={[centerText]}
            />
          </div>
        </div>

        {/* React-based legend as fallback for SSR or if plugin doesn't work */}
        <div className={`${isMobile ? 'w-full' : 'w-1/2'} space-y-2 custom-legend`}>
          <Link
            to="/projects"
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity mb-2 no-underline">
            <h4 className="font-semibold mb-2 md:mb-6">Tổng số: {projectSummary?.total}</h4>
          </Link>
          {/* Render the legend items */}
          <div className={`${isMobile ? 'grid grid-cols-2 gap-2' : 'space-y-2'}`}>
            {ProjectStatuses.map((status, index) => {
              const count = values[index];
              const color = Array.isArray(colors) ? colors[index] : '#ccc';

              return (
                <Link
                  key={status.id}
                  to={`/projects?status=${status.id}`}
                  className="flex items-center cursor-pointer hover:opacity-80 transition-opacity mb-1 no-underline">
                  <span
                    className="inline-block w-3 h-3 mr-2 rounded-sm"
                    style={{backgroundColor: color as string}}></span>
                  <span className="text-gray-800 text-sm md:text-base">
                    {status.label} ({count})
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

const TaskDoughnutChart = ({
  taskSummary,
  label,
  isMobile,
}: {
  label: string;
  taskSummary?: TaskSummary;
  isMobile: boolean;
}) => {
  const statusColors = Object.values(TaskStatusColors);

  // Process chart data after API response
  const chartData: ChartData<'doughnut'> = {
    labels: TaskStatuses.map(status => status.label),
    datasets: [
      {
        data: TaskStatuses.map(status => taskSummary?.by_status?.[status.id] || 0),
        backgroundColor: statusColors,
        borderWidth: 1,
      },
    ],
  };

  const total = taskSummary?.total || 0;
  const colors = chartData.datasets[0].backgroundColor;
  const values = chartData.datasets[0].data;

  const centerText = {
    id: 'centerText',
    beforeDraw(chart: any) {
      const {
        ctx,
        chartArea: {width, height},
      } = chart;
      ctx.save();
      ctx.font = 'bold 24px Arial';
      ctx.fillStyle = '#666';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(total.toString(), width / 2, height / 2);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#999';
      ctx.fillText('Tổng số', width / 2, height / 2 + 25);
    },
  };

  return (
    <Card className="p-4 md:p-6 w-full">
      <h3 className="text-lg font-semibold mb-4">{label || 'Công việc'}</h3>
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center gap-4 w-full`}>
        <div className={`${isMobile ? 'w-full' : 'w-1/2'} flex justify-center`}>
          <div style={{ maxWidth: '250px', width: '100%' }}>
            <Doughnut
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    display: false, // Hide the default legend
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const label = context.label || '';
                        const value = context.raw as number;
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      },
                    },
                  },
                },
                cutout: '70%', // Makes the doughnut hole bigger
              }}
              plugins={[centerText]}
            />
          </div>
        </div>

        {/* React-based legend */}
        <div className={`${isMobile ? 'w-full' : 'w-1/2'} space-y-2 custom-legend`}>
          <Link
            to="/tasks"
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity mb-2 no-underline">
            <h4 className="font-semibold mb-2 md:mb-6">Tổng số: {taskSummary?.total}</h4>
          </Link>
          {/* Render the legend items */}
          <div className={`${isMobile ? 'grid grid-cols-2 gap-2' : 'space-y-2'}`}>
            {TaskStatuses.map((status, index) => {
              const count = values[index];
              const color = Array.isArray(colors) ? colors[index] : '#ccc';

              return (
                <Link
                  key={status.id}
                  to={`/tasks?status=${status.id}`}
                  className="flex items-center cursor-pointer hover:opacity-80 transition-opacity mb-1 no-underline">
                  <span
                    className="inline-block w-3 h-3 mr-2 rounded-sm"
                    style={{backgroundColor: color as string}}></span>
                  <span className="text-gray-800 text-sm md:text-base">
                    {status.label} ({count})
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};
